import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Clock } from "lucide-react";
import { getTodayAttendance } from "../../api/attendance";
import { useNavigation } from "../../context/NavigationContext";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";
import { useMyWorkMode } from "../../hooks/useMyWorkMode";
import { isFeatureVisible } from "../../utils/featureVisibility";
import { canUseRemoteAttendance } from "../../utils/workModeHelpers";
import {
  ATTENDANCE_WALL_CLOCK_TZ,
  formatAttendanceTime,
  nowAsWallClockUtc,
} from "../../utils/attendanceTimeFormat";
import "./HomeAttendanceClock.css";

function formatElapsed(ms) {
  const safe = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
}

function formatHoursDecimal(hours) {
  if (hours == null || Number.isNaN(Number(hours))) return "—";
  const totalSec = Math.round(Number(hours) * 3600);
  return formatElapsed(totalSec * 1000);
}

function formatDateAr(value = new Date()) {
  try {
    return value.toLocaleDateString("ar-EG", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: ATTENDANCE_WALL_CLOCK_TZ,
    });
  } catch {
    return "";
  }
}

function formatNowClock(value = new Date()) {
  try {
    return value.toLocaleTimeString("ar-EG", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: ATTENDANCE_WALL_CLOCK_TZ,
    });
  } catch {
    return "—";
  }
}

function ClockDetails({
  loading,
  error,
  statusClass,
  statusLabel,
  workHoursDisplay,
  today,
  tick,
}) {
  if (loading) {
    return <p className="home-att-clock-muted">جاري تحميل بيانات الحضور...</p>;
  }

  if (error) {
    return <p className="home-att-clock-error">{error}</p>;
  }

  return (
    <>
      <div className={`home-att-clock-face home-att-clock-face-${statusClass}`}>
        <span className="home-att-clock-face-label">ساعات العمل</span>
        <strong className="home-att-clock-face-value" aria-live="polite">
          {workHoursDisplay}
        </strong>
        <span className={`home-att-clock-badge home-att-clock-badge-${statusClass}`}>
          {statusLabel}
        </span>
      </div>

      <div className="home-att-clock-metrics">
        <div className="home-att-clock-metric">
          <span className="home-att-clock-metric-label">وقت الحضور</span>
          <strong className="home-att-clock-metric-value">
            {formatAttendanceTime(today?.checkInTime)}
          </strong>
        </div>
        <div className="home-att-clock-metric">
          <span className="home-att-clock-metric-label">وقت الانصراف</span>
          <strong className="home-att-clock-metric-value">
            {formatAttendanceTime(today?.checkOutTime)}
          </strong>
        </div>
        <div className="home-att-clock-metric">
          <span className="home-att-clock-metric-label">ساعات العمل</span>
          <strong className="home-att-clock-metric-value">
            {workHoursDisplay}
          </strong>
        </div>
        <div className="home-att-clock-metric">
          <span className="home-att-clock-metric-label">الوقت الآن</span>
          <strong className="home-att-clock-metric-value">
            {formatNowClock(tick)}
          </strong>
        </div>
      </div>
    </>
  );
}

function HomeAttendanceClock() {
  const navigation = useNavigation();
  const flags = useFeatureFlags();
  const { workMode } = useMyWorkMode();
  const canPunch =
    isFeatureVisible(flags, "attendance") && canUseRemoteAttendance(workMode);
  const [today, setToday] = useState(null);
  const [handover, setHandover] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(() => new Date());
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      const data = await getTodayAttendance();
      setToday(data.today || null);
      setHandover(data.handover || null);
    } catch (err) {
      if (silent) return;
      const message =
        err.response?.data?.message ||
        err.message ||
        "تعذر تحميل بيانات الحضور";
      setError(Array.isArray(message) ? message.join("\n") : message);
      setToday(null);
      setHandover(null);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const checkedIn = Boolean(today?.checkInTime);
  const checkedOut = Boolean(today?.checkOutTime);
  const isLive = checkedIn && !checkedOut;

  useEffect(() => {
    const id = setInterval(() => setTick(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!isLive) return undefined;
    const id = setInterval(() => refresh(true), handover?.waitingForIncoming ? 15_000 : 60_000);
    return () => clearInterval(id);
  }, [isLive, refresh, handover?.waitingForIncoming]);

  useEffect(() => {
    if (!open) return undefined;

    function onKey(event) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    function onBack(event) {
      event.preventDefault();
      setOpen(false);
    }

    window.addEventListener("keydown", onKey);
    window.addEventListener("app:backbutton", onBack);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("app:backbutton", onBack);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  let workHoursDisplay = "—";
  let statusLabel = "لم يُسجَّل حضور اليوم";
  let statusClass = "idle";

  if (checkedOut) {
    workHoursDisplay =
      today?.totalWorkingHours != null
        ? formatHoursDecimal(today.totalWorkingHours)
        : formatElapsed(
            new Date(today.checkOutTime).getTime() -
              new Date(today.checkInTime).getTime(),
          );
    statusLabel = "تم الانصراف";
    statusClass = "done";
  } else if (checkedIn) {
    const start = new Date(today.checkInTime).getTime();
    const end = nowAsWallClockUtc(tick).getTime();
    workHoursDisplay = formatElapsed(end - start);
    if (handover?.waitingForIncoming) {
      statusLabel = "بانتظار استلام الشيفت";
      statusClass = "live";
    } else if (handover?.incoming?.fullName) {
      statusLabel = `${handover.incoming.fullName} استلمت — يمكن الانصراف`;
      statusClass = "live";
    } else {
      statusLabel = "جاري العمل";
      statusClass = "live";
    }
  }

  const details = (
    <ClockDetails
      loading={loading}
      error={error}
      statusClass={statusClass}
      statusLabel={statusLabel}
      workHoursDisplay={workHoursDisplay}
      today={today}
      tick={tick}
    />
  );

  return (
    <div className="home-att-clock-wrap" dir="rtl">
      <button
        type="button"
        className={`home-att-clock-chip home-att-clock-chip-${statusClass}`}
        onClick={() => {
          if (canPunch && navigation?.navigate) {
            navigation.navigate("attendance");
            return;
          }
          setOpen(true);
        }}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={
          canPunch
            ? `ساعة الحضور ${workHoursDisplay} — ${statusLabel}. اضغط لتسجيل الحضور`
            : `ساعة الحضور ${workHoursDisplay} — ${statusLabel}. اضغط لفتح التفاصيل`
        }
      >
        <Clock size={18} aria-hidden="true" />
        <span className="home-att-clock-chip-text">
          <strong className="home-att-clock-chip-value" aria-live="polite">
            {loading ? "..." : workHoursDisplay}
          </strong>
          <span className={`home-att-clock-chip-status home-att-clock-badge-${statusClass}`}>
            {statusLabel}
          </span>
        </span>
      </button>

      {open &&
        createPortal(
          <div
            className="home-att-clock-overlay"
            onClick={() => setOpen(false)}
            role="presentation"
          >
            <div
              className="home-att-clock-sheet"
              role="dialog"
              aria-modal="true"
              aria-labelledby="home-att-clock-sheet-title"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="home-att-clock-sheet-handle" aria-hidden="true" />
              <div className="home-att-clock-top">
                <div>
                  <h2
                    id="home-att-clock-sheet-title"
                    className="home-att-clock-title"
                  >
                    ساعة الحضور اليوم
                  </h2>
                  <p className="home-att-clock-date">{formatDateAr(tick)}</p>
                </div>
                <button
                  type="button"
                  className="home-att-clock-close"
                  onClick={() => setOpen(false)}
                >
                  إغلاق
                </button>
              </div>
              {details}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

export default HomeAttendanceClock;
